import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useOrgHall } from "@/hooks/useOrgHall";
import { Button } from "@/components/ui/button";
import { format, differenceInDays } from "date-fns";
import { sv } from "date-fns/locale";
import { getLocalDayBounds } from "@/lib/utils";
import { Printer } from "lucide-react";
import { formatPrice } from "@/utils/formatPrice";
import type { ReportDateRange } from "./reports/ReportDateRangePicker";
import { splitMatchiPayments, excludeMatchiPayments } from "@/lib/matchiRevenue";

interface _BookingRow {
  id: string;
  start_time: string;
  end_time?: string;
  status: string;
  court_id: string;
  duration_minutes: number | null;
  price_total: number | null;
  courts?: { name: string; hall_id: string };
  [key: string]: unknown;
}

interface _PaymentRow {
  id: string;
  amount: number;
  status: string;
  payment_method: string | null;
}

interface ManagementReportProps {
  range: ReportDateRange;
  compareRange?: ReportDateRange;
}

export default function ManagementReport({ range, compareRange }: ManagementReportProps) {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const { hallId, hallHours } = useOrgHall();
  const reportRef = useRef<HTMLDivElement>(null);

  const rangeStart = getLocalDayBounds(range.from).start;
  const rangeEnd = getLocalDayBounds(range.to).end;
  const days = differenceInDays(range.to, range.from) + 1;

  const prevStart = compareRange ? getLocalDayBounds(compareRange.from).start : rangeStart;
  const prevEnd = compareRange ? getLocalDayBounds(compareRange.to).end : rangeEnd;

  const { data: bookings = [] } = useQuery({
    queryKey: ["mgmt-report-bookings", hallId, rangeStart, rangeEnd],
    queryFn: async () => {
      const { data } = await supabase
        .from("bookings")
        .select("id, start_time, end_time, status, court_id, duration_minutes, price_total, courts!inner(name, hall_id)")
        .eq("courts.hall_id", hallId!)
        .gte("start_time", rangeStart)
        .lte("start_time", rangeEnd)
        .is("deleted_at", null);
      return data ?? [];
    },
    enabled: !!hallId,
  });

  const { data: prevBookings = [] } = useQuery({
    queryKey: ["mgmt-report-prev-bookings", hallId, prevStart, prevEnd],
    queryFn: async () => {
      const { data } = await supabase
        .from("bookings")
        .select("id, start_time, status, duration_minutes, price_total, courts!inner(hall_id)")
        .eq("courts.hall_id", hallId!)
        .gte("start_time", prevStart)
        .lte("start_time", prevEnd)
        .is("deleted_at", null);
      return data ?? [];
    },
    enabled: !!hallId && !!compareRange,
  });

  const { data: payments = [] } = useQuery({
    queryKey: ["mgmt-report-payments", organization?.id, rangeStart, rangeEnd],
    queryFn: async () => {
      const { data } = await supabase
        .from("booking_payments")
        .select("id, amount, status, payment_method, payment_method_id")
        .eq("organization_id", organization!.id)
        .eq("status", "paid")
        .gte("created_at", rangeStart)
        .lte("created_at", rangeEnd);
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  const { data: prevPayments = [] } = useQuery({
    queryKey: ["mgmt-report-prev-payments", organization?.id, prevStart, prevEnd],
    queryFn: async () => {
      const { data } = await supabase
        .from("booking_payments")
        .select("id, amount, status, payment_method, payment_method_id")
        .eq("organization_id", organization!.id)
        .eq("status", "paid")
        .gte("created_at", prevStart)
        .lte("created_at", prevEnd);
      return data ?? [];
    },
    enabled: !!organization?.id && !!compareRange,
  });

  const { data: memberCount = 0 } = useQuery({
    queryKey: ["mgmt-report-members", organization?.id],
    queryFn: async () => {
      const { count } = await supabase
        .from("organization_members")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", organization!.id);
      return count ?? 0;
    },
    enabled: !!organization?.id,
  });

  const { data: courtCount = 0 } = useQuery({
    queryKey: ["mgmt-report-courts", hallId],
    queryFn: async () => {
      const { count } = await supabase.from("courts").select("id", { count: "exact", head: true }).eq("hall_id", hallId!).eq("status", "active");
      return count ?? 0;
    },
    enabled: !!hallId,
  });

  // Compute
  const confirmed = bookings.filter((b) => b.status !== "cancelled");
  const cancelled = bookings.filter((b) => b.status === "cancelled");
  const prevConfirmed = prevBookings.filter((b) => b.status !== "cancelled");

  // C3/BES-004: MATCHi-avräknade betalningar räknas inte som Boply-intäkt —
  // de särredovisas separat så jämförelsen mot föregående period blir äpplen mot äpplen.
  const { boply: boplyPayments, matchi: matchiPayments } = splitMatchiPayments(payments);
  const boplyPrevPayments = excludeMatchiPayments(prevPayments);

  const totalRevenue = boplyPayments.reduce((s, p) => s + (p.amount || 0), 0);
  const prevTotalRevenue = boplyPrevPayments.reduce((s, p) => s + (p.amount || 0), 0);
  const matchiRevenue = matchiPayments.reduce((s, p) => s + (p.amount || 0), 0);
  const onlinePayments = boplyPayments.filter((p) => p.payment_method === "card" || p.payment_method === "swish");
  const manualPayments = boplyPayments.filter((p) => p.payment_method === "manual");
  const onlineRevenue = onlinePayments.reduce((s, p) => s + (p.amount || 0), 0);
  const manualRevenue = manualPayments.reduce((s, p) => s + (p.amount || 0), 0);

  const totalHours = Math.round(confirmed.reduce((s: number, b) => s + (b.duration_minutes || 0), 0) / 60);
  const prevTotalHours = Math.round(prevConfirmed.reduce((s: number, b) => s + (b.duration_minutes || 0), 0) / 60);

  const hoursPerDay = hallHours ? (hallHours.end - hallHours.start) : 14;
  const totalSlots = courtCount * hoursPerDay * days;
  const occupancyRate = totalSlots > 0 ? Math.round((totalHours / totalSlots) * 100) : 0;
  const prevOccupancy = totalSlots > 0 ? Math.round((prevTotalHours / totalSlots) * 100) : 0;

  const cancelRate = bookings.length > 0 ? Math.round((cancelled.length / bookings.length) * 100) : 0;
  const lostRevenue = cancelled.reduce((s: number, b) => s + (b.price_total || 0), 0);

  const courtOccupancy: Record<string, number> = {};
  confirmed.forEach((b) => {
    const name = b.courts?.name || t("report.unknown");
    courtOccupancy[name] = (courtOccupancy[name] || 0) + (b.duration_minutes || 0);
  });

  const pctChange = (curr: number, prev: number) => {
    if (prev === 0) return curr > 0 ? "+100%" : "0%";
    const change = Math.round(((curr - prev) / prev) * 100);
    return change >= 0 ? `+${change}%` : `${change}%`;
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow || !reportRef.current) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html><head><title>${t("report.managementReport")} - ${organization?.name}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 40px; color: #1a1a1a; }
        .header { display: flex; align-items: center; gap: 16px; margin-bottom: 32px; border-bottom: 2px solid #e5e5e5; padding-bottom: 16px; }
        .header h1 { font-size: 24px; }
        .header .period { color: #666; font-size: 14px; }
        .kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 24px; }
        .kpi { border: 1px solid #e5e5e5; border-radius: 8px; padding: 16px; }
        .kpi-label { font-size: 12px; color: #666; margin-bottom: 4px; }
        .kpi-value { font-size: 24px; font-weight: 700; }
        .kpi-change { font-size: 12px; margin-top: 4px; }
        .kpi-change.positive { color: #16a34a; }
        .kpi-change.negative { color: #dc2626; }
        .section { margin-bottom: 24px; }
        .section h2 { font-size: 16px; font-weight: 600; margin-bottom: 12px; border-bottom: 1px solid #e5e5e5; padding-bottom: 4px; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th, td { padding: 8px 12px; text-align: left; border-bottom: 1px solid #f0f0f0; }
        th { font-weight: 600; color: #666; font-size: 11px; text-transform: uppercase; }
        .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e5e5; font-size: 11px; color: #999; text-align: center; }
        @media print { body { padding: 20px; } }
      </style></head><body>
      ${reportRef.current.innerHTML}
      </body></html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  const periodLabel = `${format(range.from, "d MMM yyyy", { locale: sv })} – ${format(range.to, "d MMM yyyy", { locale: sv })}`;

  return (
    <div>
      <Button onClick={handlePrint} variant="outline" size="sm" className="gap-1.5">
        <Printer className="h-4 w-4" /> {t("report.downloadReport")}
      </Button>

      <div ref={reportRef} className="hidden">
        <div className="header">
          {organization?.logo_url && <img src={organization.logo_url} alt="" style={{ height: 40, width: 40, borderRadius: 8 }} />}
          <div>
            <h1>{organization?.name} — {t("report.managementReport")}</h1>
            <div className="period">{periodLabel}</div>
          </div>
        </div>

        <div className="kpi-grid">
          <div className="kpi">
            <div className="kpi-label">{t("report.totalRevenue")}</div>
            <div className="kpi-value">{formatPrice(totalRevenue)}</div>
            {compareRange && (
              <div className={`kpi-change ${totalRevenue >= prevTotalRevenue ? "positive" : "negative"}`}>
                {pctChange(totalRevenue, prevTotalRevenue)} {t("report.vsPreviousPeriod")}
              </div>
            )}
          </div>
          <div className="kpi">
            <div className="kpi-label">{t("report.onlinePayments")}</div>
            <div className="kpi-value">{formatPrice(onlineRevenue)}</div>
            <div className="kpi-change">{onlinePayments.length} st</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">{t("report.manualPayments")}</div>
            <div className="kpi-value">{formatPrice(manualRevenue)}</div>
            <div className="kpi-change">{manualPayments.length} st</div>
          </div>
          {/* C3: särredovisning. Egen kpi-ruta istället för MatchiSettledRow — den här
              rapporten skrivs ut via innerHTML i ett nytt fönster utan Tailwind. */}
          {matchiRevenue > 0 && (
            <div className="kpi">
              <div className="kpi-label">{t("reports.matchi.settled")}</div>
              <div className="kpi-value">{formatPrice(matchiRevenue)}</div>
              <div className="kpi-change">{t("reports.matchi.hint")}</div>
            </div>
          )}
        </div>

        <div className="kpi-grid">
          <div className="kpi">
            <div className="kpi-label">{t("report.occupancyRate")}</div>
            <div className="kpi-value">{occupancyRate}%</div>
            {compareRange && (
              <div className={`kpi-change ${occupancyRate >= prevOccupancy ? "positive" : "negative"}`}>
                {pctChange(occupancyRate, prevOccupancy)} {t("report.vsPrevious")}
              </div>
            )}
          </div>
          <div className="kpi">
            <div className="kpi-label">{t("report.bookingCount")}</div>
            <div className="kpi-value">{confirmed.length}</div>
            {compareRange && (
              <div className={`kpi-change ${confirmed.length >= prevConfirmed.length ? "positive" : "negative"}`}>
                {pctChange(confirmed.length, prevConfirmed.length)} {t("report.vsPrevious")}
              </div>
            )}
          </div>
          <div className="kpi">
            <div className="kpi-label">{t("report.registeredMembers")}</div>
            <div className="kpi-value">{memberCount}</div>
          </div>
        </div>

        <div className="kpi-grid">
          <div className="kpi">
            <div className="kpi-label">{t("report.cancellations")}</div>
            <div className="kpi-value">{cancelled.length} ({cancelRate}%)</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">{t("report.lostRevenue")}</div>
            <div className="kpi-value">{formatPrice(lostRevenue)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">{t("report.hoursPlayed")}</div>
            <div className="kpi-value">{totalHours}h</div>
          </div>
        </div>

        <div className="section">
          <h2>{t("report.occupancyPerCourt")}</h2>
          <table>
            <thead><tr><th>{t("report.court")}</th><th>{t("report.hours")}</th><th>{t("report.occupancy")}</th></tr></thead>
            <tbody>
              {Object.entries(courtOccupancy).map(([name, mins]) => {
                const hours = Math.round(mins / 60);
                const courtSlots = hoursPerDay * days;
                const rate = courtSlots > 0 ? Math.round((hours / courtSlots) * 100) : 0;
                return (
                  <tr key={name}>
                    <td>{name}</td>
                    <td>{hours}h</td>
                    <td>{rate}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="footer">
          {t("report.generated")} {format(new Date(), "d MMMM yyyy HH:mm", { locale: sv })} · {organization?.name}
        </div>
      </div>
    </div>
  );
}
